import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import SectionTab from '../../../../common/sectiontab/SectionTab';
import RentalParkingTable from './components/RentalParkingTable';
import apiServices from '../../../../../services/apiService';

const RentalParkingList = () => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const roleId = Number(user?.roleId || 3);
    const [rentalData, setRentalData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr.includes("0001-01-01")) return "—";
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    useEffect(() => {
        const fetchRentalData = async () => {
            const siteId = user?.siteId;
            const unitId = user?.unitId || user?.branchorunitID;
            const isUnitAdmin = roleId === 4 || roleId === 6;

            let endpoint = '';
            if (isUnitAdmin) {
                if (!unitId) return;
                endpoint = `/RentalParking/unit/${unitId}`;
            } else {
                if (!siteId) return;
                endpoint = `/RentalParking/site/${siteId}`;
            }

            try {
                const res = await apiServices({
                    method: 'GET',
                    url: endpoint
                });

                if (res?.data?.isSuccess === true || res?.data?.IsSuccess === true) {
                    const responseData = res?.Data || res?.data?.Data || [];
                    const formattedData = responseData.map(item => ({
                        id: (item.Id || item.id || "").toString(),
                        slot: item.SlotNumber || item.slotNumber || "—",
                        rentedFrom: item.OwnerUnitName || item.ownerUnitName || (item.OwnerUnitId || item.ownerUnitId ? `Unit #${item.OwnerUnitId || item.ownerUnitId}` : "Site / Management"),
                        rentedTo: item.RenterUnitName || item.renterUnitName || (item.RenterUnitId || item.renterUnitId ? `Unit #${item.RenterUnitId || item.renterUnitId}` : "— (Awaiting Renter)"),
                        rentalPeriodStart: formatDate(item.StartDate || item.startDate),
                        rentalPeriodEnd: formatDate(item.EndDate || item.endDate),
                        status: item.Status || item.status || "Unknown"
                    }));
                    setRentalData(formattedData);
                } else {
                    toast.error("Failed to load rental parking data");
                }
            } catch (error) {
                console.error("Error fetching rental data", error);
                toast.error("Error loading rental data");
            } finally {
                setIsLoading(false);
            }
        };

        fetchRentalData();
    }, [user, roleId]);

    const allData = rentalData;
    const pendingData = rentalData.filter(d => d.status === 'Pending' || d.status === 'PendingApproval');
    const activeData = rentalData.filter(d => d.status === 'Active');
    const completedData = rentalData.filter(d => d.status === 'Completed' || d.status === 'Rejected');

    const tabs = [
        { key: 'all', label: `All Lease Offers (${allData.length})`, component: () => <RentalParkingTable data={allData} title="All Lease Offers" /> },
        { key: 'pending', label: `Pending Response (${pendingData.length})`, component: () => <RentalParkingTable data={pendingData} title="Pending Responses" /> },
        { key: 'active', label: `Active Contracts (${activeData.length})`, component: () => <RentalParkingTable data={activeData} title="Active Contracts" /> },
        { key: 'completed', label: `Completed / Rejected (${completedData.length})`, component: () => <RentalParkingTable data={completedData} title="Completed / Rejected Leases" /> }
    ];

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Rental Parking"
                subHeading="Review security gate visitor requests, allocate guest slots, and pre-approve client passes"
                primaryBtnText={(roleId === 4 || roleId === 6) ? "Offer Lease to Site" : undefined}
                primaryBtnIcon={(roleId === 4 || roleId === 6) ? faPlus : undefined}
                PrimaryOnClick={(roleId === 4 || roleId === 6) ? () => navigate('/zentres/parking-management/rental-parking/offer') : undefined}
            />

            {/* Metrics Overview Card */}
            <div className="mt-6 bg-white dark:bg-cardbg border border-bordercolor rounded-xl p-5 shadow-sm">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-5">
                    <div>
                        <h2 className="text-xs font-bold text-gray-900 dark:text-textprimary">Rental Parking Overview</h2>
                        <p className="text-[11px] text-gray-500 mt-1">Real-time status of commercial parking leases and active yields</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl border bg-gray-50 dark:bg-actionitembg border-gray-200 dark:border-bordercolor">
                        <div className="flex justify-between items-start mb-3">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Available</span>
                            <span className="text-[9px] font-bold px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 shadow-sm">
                                {allData.length} Slots
                            </span>
                        </div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-textprimary">{allData.length}</h3>
                        <p className="text-[11px] mt-1 font-medium text-gray-500 flex items-center gap-1"><FontAwesomeIcon icon={faInfoCircle} /> Total Offers</p>
                    </div>
                    
                    <div className="p-4 rounded-xl border bg-[#FFFBEB] dark:bg-yellow-900/10 border-[#FDE68A] dark:border-yellow-700/30">
                        <div className="flex justify-between items-start mb-3">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Pending</span>
                            <span className="text-[9px] font-bold px-2.5 py-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 shadow-sm">
                                {pendingData.length} Requests
                            </span>
                        </div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-textprimary">{pendingData.length}</h3>
                        <p className="text-[11px] mt-1 font-medium text-yellow-600 dark:text-yellow-500 flex items-center gap-1"><FontAwesomeIcon icon={faInfoCircle} /> Awaiting Site Acceptance</p>
                    </div>

                    <div className="p-4 rounded-xl border bg-[#F0FDF4] dark:bg-green-900/10 border-[#BBF7D0] dark:border-green-700/30">
                        <div className="flex justify-between items-start mb-3">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Active</span>
                            <span className="text-[9px] font-bold px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 shadow-sm">
                                {activeData.length} Leases
                            </span>
                        </div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-textprimary">{activeData.length}</h3>
                        <p className="text-[11px] mt-1 font-medium text-green-600 dark:text-green-500 flex items-center gap-1"><FontAwesomeIcon icon={faInfoCircle} /> Signed & Paid</p>
                    </div>

                    <div className="p-4 rounded-xl border bg-gray-50 dark:bg-actionitembg border-gray-200 dark:border-bordercolor">
                        <div className="flex justify-between items-start mb-3">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Completed</span>
                            <span className="text-[9px] font-bold px-2.5 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 shadow-sm">
                                {completedData.length} Completed
                            </span>
                        </div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-textprimary">{completedData.length}</h3>
                        <p className="text-[11px] mt-1 font-medium text-gray-500 flex items-center gap-1"><FontAwesomeIcon icon={faInfoCircle} /> Past / Declined Leases</p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="mt-2">
                <SectionTab tabs={tabs} defaultTab="all" />
            </div>
        </div>
    );
};

export default RentalParkingList;
